import { developerService } from '@/services/developerService'

export default {
    namespaced: true,
    state: {
        status: null,
        n8nStatus: null,
        mongoStatus: null,
        loading: false,
        error: null
    },
    getters: {
        getStatus: state => state.status,
        getN8NStatus: state => state.n8nStatus,
        getMongoStatus: state => state.mongoStatus,
        isLoading: state => state.loading,
        getError: state => state.error,

        // Computed getters for specific data
        isMongoConnected: state => {
            return state.mongoStatus?.success === true
        },

        getMongoCollections: state => {
            return state.mongoStatus?.collections || []
        },

        getMongoDatabaseName: state => {
            return state.mongoStatus?.database || null
        }
    },
    mutations: {
        SET_STATUS(state, status) {
            state.status = status
        },
        SET_N8N_STATUS(state, n8nStatus) {
            state.n8nStatus = n8nStatus
        },
        SET_MONGO_STATUS(state, mongoStatus) {
            state.mongoStatus = mongoStatus
        },
        SET_LOADING(state, loading) {
            state.loading = loading
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
        }
    },
    actions: {
        /**
         * Fetch developer status
         */
        async fetchStatus({ commit }) {
            commit('SET_LOADING', true)
            commit('CLEAR_ERROR')

            try {
                const status = await developerService.getStatus()
                commit('SET_STATUS', status)
            } catch (error) {
                // Instead of setting a global error, create a status object with error info
                const errorStatus = {
                    success: false,
                    status: error.response?.status || 500,
                    message: error.response?.data?.message || error.message || 'API status check failed',
                    timestamp: new Date().toISOString(),
                    error: error.response?.data?.error || error.message
                }
                commit('SET_STATUS', errorStatus)
                // Don't throw the error to prevent it from being displayed as a global error
            } finally {
                commit('SET_LOADING', false)
            }
        },

        /**
         * Fetch N8N developer status
         */
        async fetchN8NStatus({ commit }) {
            commit('SET_LOADING', true)
            commit('CLEAR_ERROR')

            try {
                const n8nStatus = await developerService.getN8NStatus()
                commit('SET_N8N_STATUS', n8nStatus)
            } catch (error) {
                // Instead of setting a global error, create an n8n status object with error info
                const errorStatus = {
                    success: false,
                    status: error.response?.status || 500,
                    message: error.response?.data?.message || error.message || 'N8N connection test failed',
                    timestamp: new Date().toISOString(),
                    error: error.response?.data?.error || error.message
                }
                commit('SET_N8N_STATUS', errorStatus)
                // Don't throw the error to prevent it from being displayed as a global error
            } finally {
                commit('SET_LOADING', false)
            }
        },

        /**
         * Fetch MongoDB status
         */
        async fetchMongoStatus({ commit }) {
            commit('SET_LOADING', true)
            commit('CLEAR_ERROR')

            try {
                const mongoStatus = await developerService.getMongoStatus()
                commit('SET_MONGO_STATUS', mongoStatus)
            } catch (error) {
                // Instead of setting a global error, create a mongo status object with error info
                const errorStatus = {
                    success: false,
                    status: error.response?.status || 500,
                    message: error.response?.data?.message || error.message || 'MongoDB connection check failed',
                    timestamp: new Date().toISOString(),
                    error: error.response?.data?.error || error.message
                }
                commit('SET_MONGO_STATUS', errorStatus)
                // Don't throw the error to prevent it from being displayed as a global error
            } finally {
                commit('SET_LOADING', false)
            }
        },

        /**
         * Fetch all developer statuses
         */
        async fetchAllStatuses({ dispatch }) {
            // Use Promise.allSettled to handle individual failures gracefully
            const results = await Promise.allSettled([
                dispatch('fetchStatus'),
                dispatch('fetchN8NStatus'),
                dispatch('fetchMongoStatus')
            ]);
            
            // Log any rejected promises for debugging
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    const services = ['Status', 'N8N', 'MongoDB']
                    console.warn(`${services[index]} fetch failed:`, result.reason)
                }
            });
        }
    }
} 